import { ArrowRight, Send, Trash2, X } from 'lucide-react';

import { FileTypeIcon } from '@/components/transfer/file-icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatBytes, truncateMiddle } from '@flowshare/shared';
import { usePendingFilesStore } from '@/stores/pending-files-store';

interface PendingTrayProps {
  title?: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  actionIcon?: 'send' | 'arrow';
  className?: string;
}

export function PendingTray({
  title = 'Ready to send',
  hint,
  actionLabel,
  onAction,
  actionDisabled,
  actionIcon = 'send',
  className,
}: PendingTrayProps) {
  const files = usePendingFilesStore((state) => state.files);
  const remove = usePendingFilesStore((state) => state.remove);
  const clear = usePendingFilesStore((state) => state.clear);

  if (files.length === 0) return null;
  const total = files.reduce((sum, file) => sum + file.size, 0);

  return (
    <div className={cn('surface overflow-hidden rounded-2xl', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
        <div className="flex items-baseline gap-2.5">
          <h3 className="text-[13px] font-semibold">{title}</h3>
          <span className="text-[11px] text-muted-foreground">
            {files.length} file{files.length === 1 ? '' : 's'} · {formatBytes(total)}
          </span>
        </div>
        <Button variant="ghost" size="xs" onClick={clear} className="text-muted-foreground">
          <Trash2 className="size-3" />
          Clear
        </Button>
      </div>

      <ul className="max-h-52 divide-y divide-border/40 overflow-y-auto">
        {files.map((file, index) => {
          const path = (file as File & { relativePath?: string; webkitRelativePath?: string })
            .relativePath ?? (file as File & { webkitRelativePath?: string }).webkitRelativePath;
          return (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="group flex items-center gap-3 px-4 py-2.5"
            >
              <FileTypeIcon name={file.name} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-medium leading-tight">
                  {truncateMiddle(file.name, 56)}
                </p>
                <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground/70">
                  {path && path !== file.name ? path : formatBytes(file.size)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => remove(index)}
                aria-label={`Remove ${file.name}`}
                className="opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
              >
                <X className="size-3" />
              </Button>
            </li>
          );
        })}
      </ul>

      {onAction ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 bg-muted/20 px-4 py-3">
          <p className="text-[11px] text-muted-foreground">
            {hint ?? 'Files are read directly from disk — never uploaded.'}
          </p>
          <Button size="sm" onClick={onAction} disabled={actionDisabled}>
            {actionIcon === 'send' ? <Send className="size-3.5" /> : <ArrowRight className="size-3.5" />}
            {actionLabel ?? 'Send files'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
